import { Injectable } from '@angular/core';
import DailyIframe from '@daily-co/daily-js';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class RecordingService {
    /**
     * BehaviorSubject to keep track of the recording status
     *  - Used in [handleParticipantLeft()]{@link StreamProviderService#handleParticipantLeft} in the StreamProviderService
     *  - Set in [handleRecordingStarted()]{@link StreamProviderService#handleRecordingStarted} in the StreamProviderService
     *  - Set in [handleRecordingStopped()]{@link StreamProviderService#handleRecordingStopped} in the StreamProviderService
     *  - Sets [isRecording]{@link RoomComponent#isRecording} in the RoomComponen
     *  - Sets [recordingStatus]{@link RoomComponent#recordingStatus} in the RoomComponent
     *  - Subscribed to in [setupEvents()]{@link RoomComponent#setupEvents} in the RoomComponent
     *  - Sets [isRecording]{@link ParticipantGroupComponent#isRecording} in the ParticipantGroupComponent
     *  - Subscribed to in [ngOnInit()]{@link ParticipantGroupComponent#ngOnInit} in the ParticipantGroupComponent
     */
    isRecording = new BehaviorSubject(false);

    isLoadingRecording = new BehaviorSubject(false);

    autoRecord = new BehaviorSubject(false);

    recordingId = new BehaviorSubject<string | null>(null);

    /**
     * Subject to emit recording errors
     *  - Emitted in [handleRecordingError()]{@link StreamProviderService#handleRecordingError} in the StreamProviderService
     */
    recordingError = new Subject<any>();

    /**
     * Gets the call object from the DailyIframe
     *  - Used in [startRecording()]{@link RecordingService#startRecording} in the RecordingService
     * - Used in [stopRecording()]{@link RecordingService#stopRecording} in the RecordingService
     */
    public get callObject(): any {
        return DailyIframe.getCallInstance();
    }

    constructor() {}

    /**
     * Starts the recording and transcription using the [callObject]{@link RecordingService#callObject}
     *  - Called in [setupTask]{@link RoomComponent#setupTask} in the RoomComponent
     *  - Called in [toggleRecording()]{@link ParticipantGroupComponent#toggleRecording} in the ParticipantGroupComponent
     */
    startRecording() {
        // callObject is DailyIframe.getCallInstance(), which is null after
        // teardown; a retry firing then would throw. Capture and bail if gone.
        const callObject = this.callObject;
        if (!callObject) {
            return;
        }
        this.isLoadingRecording.next(true);
        callObject.startRecording({
            minIdleTimeOut: 3600000,
            layout: {
                preset: 'active-participant',
            },
        });
        callObject.startTranscription();
    }

    /**
     * Stops the recording and transcription using the [callObject]{@link RecordingService#callObject}
     *  - Called in [handleParticipantLeft()]{@link StreamProviderService#handleParticipantLeft} in the StreamProviderService
     *  - Called in [toggleRecording()]{@link ParticipantGroupComponent#toggleRecording} in the ParticipantGroupComponent
     */
    stopRecording() {
        const callObject = this.callObject;
        if (!callObject) {
            return;
        }
        this.isLoadingRecording.next(false);
        callObject.stopRecording();
        callObject.stopTranscription();
    }
}
